import type { DataRow } from "../types";
import { reportedDateTime } from "./globalDateRange";
import { normalizeDmaCode, type SourceInfluenceSnapshot } from "./sourceInfluence";

export type IssueAnalysisType = "water-quality" | "construction";
export type IssueReviewStatus = "confirmed" | "rejected";
export type IssueClassificationReview = {
  row_id: string;
  analysis_type: IssueAnalysisType;
  suggested_label: string;
  status: IssueReviewStatus;
  updated_by: string;
  updated_at: string;
};

export type IssueMatch = {
  row: DataRow;
  label: string;
  keywords: string[];
  dateTime: string;
  date: string;
  hour: number | null;
  dayType: "weekday" | "saturday" | "sunday" | "unknown";
  dmaCode: string;
  branchNumber: string;
  pressurePoint: string;
  allocations: { station: string; factor: number }[];
  review: IssueClassificationReview | null;
};

const WATER_RULES = [
  { label: "น้ำขุ่น/สีผิดปกติ", terms: ["น้ำขุ่น", "น้ำแดง", "น้ำดำ", "สีเหลือง", "สีผิดปกติ", "ตะกอน", "สนิม"] },
  { label: "น้ำมีกลิ่น", terms: ["น้ำมีกลิ่น", "กลิ่นเหม็น", "เหม็น", "กลิ่นคลอรีน", "กลิ่นโคลน", "กลิ่นผิดปกติ"] },
] as const;

const CONSTRUCTION_TERMS = ["ก่อสร้าง", "ขุดถนน", "งานโยธา", "ผู้รับเหมา", "รถแบคโฮ", "แบคโฮ", "ตอกเสาเข็ม", "เจาะถนน", "วางท่อ", "เทคอนกรีต", "ปรับปรุงถนน", "งานรถไฟฟ้า"];

function normalizedText(row: DataRow): string {
  return [row.workType, row.reportedDetail, row.workDetail, row.address, row.road, row.soi]
    .map((value) => String(value ?? "").normalize("NFKC").toLocaleLowerCase("th"))
    .join(" ");
}

function normalizeTerm(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase("th");
}

export function suggestIssue(row: DataRow, type: IssueAnalysisType): { label: string; keywords: string[] } | null {
  const text = normalizedText(row);
  if (type === "water-quality") {
    for (const rule of WATER_RULES) {
      const keywords = rule.terms.filter((term) => text.includes(normalizeTerm(term)));
      if (keywords.length) return { label: rule.label, keywords };
    }
    return null;
  }
  const keywords = CONSTRUCTION_TERMS.filter((term) => text.includes(normalizeTerm(term)));
  return keywords.length ? { label: "ปัญหาที่เกี่ยวข้องกับงานก่อสร้าง", keywords } : null;
}

export function issueDayType(value: string): IssueMatch["dayType"] {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "unknown";
  const day = parsed.getDay();
  return day === 0 ? "sunday" : day === 6 ? "saturday" : "weekday";
}

export function buildIssueMatches(
  rows: DataRow[],
  type: IssueAnalysisType,
  snapshot: SourceInfluenceSnapshot | null,
  reviews: IssueClassificationReview[] = [],
): IssueMatch[] {
  const reviewByRow = new Map(reviews.filter((item) => item.analysis_type === type).map((item) => [item.row_id, item]));
  const recordByDma = new Map((snapshot?.records ?? []).map((record) => [normalizeDmaCode(record.dmaCode), record]));
  const seen = new Set<string>();
  const result: IssueMatch[] = [];
  for (const row of rows) {
    const key = String(row.jobId || row.__rowId);
    if (seen.has(key)) continue;
    const suggestion = suggestIssue(row, type);
    const review = reviewByRow.get(row.__rowId) ?? null;
    if ((!suggestion && review?.status !== "confirmed") || review?.status === "rejected") continue;
    seen.add(key);
    const dateTime = reportedDateTime(row) ?? "";
    const parsed = new Date(dateTime);
    const dmaCode = normalizeDmaCode(row.dmaCode);
    const record = recordByDma.get(dmaCode);
    const sources = record?.sources ?? [];
    const total = sources.reduce((sum, source) => sum + Math.max(0, Number(source.normalizedFactor || 0)), 0);
    result.push({
      row,
      label: review?.suggested_label || suggestion?.label || (type === "water-quality" ? "ข้อร้องเรียนคุณภาพน้ำ" : "ปัญหาที่เกี่ยวข้องกับงานก่อสร้าง"),
      keywords: suggestion?.keywords ?? [],
      dateTime,
      date: dateTime.slice(0, 10),
      hour: Number.isNaN(parsed.getTime()) ? null : Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", hour: "2-digit", hour12: false }).format(parsed)) % 24,
      dayType: issueDayType(dateTime),
      dmaCode,
      branchNumber: String(row.branchNumber ?? record?.branchNumber ?? ""),
      pressurePoint: String(record?.pressurePoint ?? "ไม่ระบุจุดวัดแรงดัน"),
      allocations: sources.length ? sources.map((source) => ({ station: source.name, factor: total > 0 ? Number(source.normalizedFactor || 0) / total : 1 / sources.length })) : [{ station: "ไม่ระบุสถานีสูบจ่าย", factor: 1 }],
      review,
    });
  }
  return result;
}

export function repeatedIssueDmas(matches: IssueMatch[], minCount: number, minDays: number): Set<string> {
  const groups = new Map<string, { count: number; days: Set<string> }>();
  for (const item of matches) {
    const key = item.dmaCode || "__unknown__";
    const group = groups.get(key) ?? { count: 0, days: new Set<string>() };
    group.count += 1;
    if (item.date) group.days.add(item.date);
    groups.set(key, group);
  }
  return new Set([...groups].filter(([, group]) => group.count >= minCount && group.days.size >= minDays).map(([dma]) => dma));
}

